import { InfoCircleIcon } from '@assets/icons';
import { useTranslation } from 'react-i18next';
import { AppLinkButton } from '@components/ui/AppLinkButton/AppLinkButton';
import { MovingBorder } from '@components/ui/moving-border';
import styles from './AppInactiveProHeaderBanner.module.css';

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

export type AppInactiveProHeaderBannerProps = {
  variant: 'inactive_pro' | 'grant_expiring' | 'scheduled_cancel';
  organizationLabel: string;
  billingPath: string;
  grantEndsAt?: string | null;
  planName?: string;
  isMobile?: boolean;
};

export const AppInactiveProHeaderBanner = ({
  variant,
  organizationLabel,
  billingPath,
  grantEndsAt,
  planName,
  isMobile = false,
}: AppInactiveProHeaderBannerProps) => {
  const { t } = useTranslation();
  const translationPrefix =
    variant === 'grant_expiring'
      ? 'organizationBilling.grantExpiringNudge'
      : variant === 'scheduled_cancel'
        ? 'organizationBilling.scheduledCancelNudge'
      : 'organizationBilling.inactiveProResume';
  const formattedGrantEndsAt = formatDateTime(grantEndsAt);
  const paidPlanName = planName || 'Pro';

  return (
    <div
      className={`${styles.root} ${isMobile ? styles.compact : ''}`}
      data-testid="inactive-pro-header-banner"
    >
      <div className={styles.borderLayer} aria-hidden>
        <MovingBorder duration={3800} rx="0" ry="0">
          <div className={styles.borderDot} />
        </MovingBorder>
      </div>
      <div className={styles.inner}>
        <div className={styles.main}>
          <InfoCircleIcon className={styles.icon} aria-hidden />
          <div className={styles.textBlock}>
            <div className={styles.line}>
              {t(`${translationPrefix}.bannerMessage`, {
                organization: organizationLabel,
                date: formattedGrantEndsAt,
                plan: paidPlanName,
              })}{' '}
              <AppLinkButton to={billingPath}>
                {t(`${translationPrefix}.bannerCta`)}
              </AppLinkButton>
            </div>
          </div>
        </div>
        <span className={styles.actionRequired}>
          {t(`${translationPrefix}.bannerActionRequired`)}
        </span>
      </div>
    </div>
  );
};
